import React from 'react';
import { Portal, Root } from '@radix-ui/react-dropdown-menu';
import HBMenuList from '../HBMenu/HBMenuList';
import { StyledArrow, StyledContent, StyledTrigger } from './HBMenu.styles';
import { MustIncludeOneOfBasicTestIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { HBMenuProps } from './HBMenu.types';
import { preventDefault } from '../../utils/shared.utils';
import { HBList } from '../HBList/HBList';
import { useMenuPointerEvents } from './useMenuPointerEvents';

const HBMenu = ({
    testIdName,
    testId,
    target,
    menuItems,
    size = 'sm',
    subMenuSize,
    hideArrowIconInSubmenu,
    isListItemContentFullWidth,
    placement = 'bottom',
    disabled,
    targetContainerStyle,
    targetAsChild = false,
    offset,
    alignOffset,
    withArrow = true,
    align = 'center',
    preventClickPropogation,
    open: controlledOpen,
    onOpenChange,
    preventFocusOnClose,
    modal = false,
    maxWidth = '500px',
    minWidth = '200px',
    width,
    maxHeight = '500px',
    slots,
    className
}: HBMenuProps & MustIncludeOneOfBasicTestIdProps) => {
    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'menu',
        componentName: 'HBMenu'
    });

    const { open, handleOpenChange, handlePointerDown, handlePointerUp } =
        useMenuPointerEvents({
            open: controlledOpen,
            onOpenChange,
            disabled
        });

    const jsx = (
        <Root open={open} onOpenChange={handleOpenChange} modal={modal}>
            <StyledTrigger
                disabled={disabled}
                style={targetContainerStyle}
                data-testid={finalTestId ? `${finalTestId}-button` : undefined}
                asChild={targetAsChild}
                $resetStyles={!targetAsChild}
                data-prevent-focus-visible={!targetAsChild || null}
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
            >
                {target}
            </StyledTrigger>

            <Portal>
                <StyledContent
                    align={align}
                    side={placement}
                    sideOffset={offset}
                    alignOffset={alignOffset}
                    className={className}
                    data-testid={finalTestId}
                    $withArrow={withArrow}
                    $maxWidth={maxWidth}
                    $minWidth={minWidth}
                    $width={width}
                    $maxHeight={maxHeight}
                    onCloseAutoFocus={
                        preventFocusOnClose ? preventDefault : undefined
                    }
                    // hideWhenDetached={true}
                >
                    <HBList
                        maxHeight={maxHeight}
                        disableKeyboardNavigation
                        slots={{
                            header: slots?.listHeader?.(),
                            footer: slots?.listFooter?.()
                        }}
                    >
                        <HBMenuList
                            menuItems={menuItems}
                            size={size}
                            subMenuSize={subMenuSize}
                            hideArrowIconInSubmenu={hideArrowIconInSubmenu}
                            isListItemContentFullWidth={
                                isListItemContentFullWidth
                            }
                        />
                        {withArrow && <StyledArrow />}
                    </HBList>
                </StyledContent>
            </Portal>
        </Root>
    );
    return preventClickPropogation ? (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div onClick={ev => ev.stopPropagation()}>{jsx}</div>
    ) : (
        jsx
    );
};

export default HBMenu;
